import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  mergeClasses
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { MAX_GROUP_DISPLAY_NAME_LENGTH } from '../../../models/schemas/group-request.schema';
import { ValidationMessageList } from '../../shared-components';

interface AddOrEditGroupModalProps {
  isModalOpen: boolean;
  onDismissModal(): void;
  onSaveButtonClicked(): void;
}

const AddOrEditGroupModalImpl: React.FC<AddOrEditGroupModalProps> = (props) => {
  const {
    isModalOpen,
    onDismissModal,
    onSaveButtonClicked,
  } = props;

  const {
    manageAwardListPageUiStore,
  } = useStores();

  const commonStyles = useCommonStyles();

  const [isBeingValidated, setIsBeingValidated] = useState<boolean>(false);

  const handleSave = (): void => {
    setIsBeingValidated(true);

    if (!manageAwardListPageUiStore.groupRequestValidation.isValid) return;

    onSaveButtonClicked();
    onDismissModal();
  };

  useEffect(() => {
    setIsBeingValidated(false);
  }, []);

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={(_, data) => {
        if (!data.open) onDismissModal();
      }}
    >
      <DialogSurface
        className={mergeClasses(commonStyles.modalSurface, 'add-or-edit-group-modal')}
      >
        <DialogBody
          className={commonStyles.modalBody}
        >
          <DialogTitle
            as='h1'
            action={
              <Button
                appearance='subtle'
                aria-label='close'
                icon={<Dismiss24Regular />}
                onClick={() => onDismissModal()}
              />
            }
          >
            {
              manageAwardListPageUiStore.addOrEditGroupModalType === 'add' && 'Add Group'
            }
            {
              manageAwardListPageUiStore.addOrEditGroupModalType === 'update' && 'Update Group'
            }
          </DialogTitle>
          <DialogContent>
            <Field
              required
              label={{ children: 'Group name', className: commonStyles.inputFieldLabel }}
              validationMessage={
                isBeingValidated
                && manageAwardListPageUiStore.groupRequestValidation.fieldErrors.displayName?.length
                  ? (
                    <ValidationMessageList
                      messages={manageAwardListPageUiStore.groupRequestValidation.fieldErrors.displayName}
                    />
                  )
                  : undefined
              }
              validationMessageIcon={null}
              hint={`${manageAwardListPageUiStore.groupRequestBody?.displayName.length || 0}/${MAX_GROUP_DISPLAY_NAME_LENGTH}`}
            >
              <Input
                value={manageAwardListPageUiStore.groupRequestBody?.displayName || ''}
                className={commonStyles.inputField}
                maxLength={MAX_GROUP_DISPLAY_NAME_LENGTH}
                onChange={(_, data) => manageAwardListPageUiStore.setGroupRequestBody({ key: 'displayName', value: data.value })}
              />
            </Field>
          </DialogContent>
          <DialogActions>
            <Button
              appearance='secondary'
              onClick={() => onDismissModal()}
            >
              Back
            </Button>
            <Button
              appearance='primary'
              disabled={isBeingValidated && !manageAwardListPageUiStore.groupRequestValidation.isValid}
              onClick={() => handleSave()}
            >
              {
                manageAwardListPageUiStore.addOrEditGroupModalType === 'add' && 'Add'
              }
              {
                manageAwardListPageUiStore.addOrEditGroupModalType === 'update' && 'Update'
              }
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const AddOrEditGroupModal = observer(AddOrEditGroupModalImpl);
